import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Button as UiButton, Dialog } from '../ui';

export interface ExpireGraceArtwork {
  src?: string;
  label?: string;
}

export interface ExpireGraceModalProps {
  open: boolean;
  trackCount: number;
  mbUsed: number;
  artworks?: ExpireGraceArtwork[];
  onSignup: () => void;
  onRemindLater: () => void;
  headline?: string;
}

const AMBER = '#E3A549';
const AMBER_INK = '#2a1d07';
const BORDER = '#26292B';

const Thumbnail: React.FC<{ art?: ExpireGraceArtwork }> = ({ art }) => (
  <div
    className="grid h-16 w-16 place-items-center overflow-hidden rounded-[10px]"
    style={{
      border: `1px solid ${BORDER}`,
      background:
        'repeating-linear-gradient(135deg, #14171d, #14171d 11px, #181c23 11px, #181c23 22px)',
    }}
  >
    {art?.src ? (
      <img src={art.src} alt={art.label ?? ''} className="h-full w-full object-cover" />
    ) : (
      <div
        style={{
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          fontSize: 9,
          color: '#5b626a',
          letterSpacing: '0.04em',
        }}
      >
        {art?.label ?? 'art'}
      </div>
    )}
  </div>
);

const ExpireGraceModal: React.FC<ExpireGraceModalProps> = ({
  open,
  trackCount,
  mbUsed,
  artworks = [],
  onSignup,
  onRemindLater,
  headline = 'Your guest session ends tomorrow',
}) => {
  const shown = artworks.slice(0, 3);
  const overflow = Math.max(0, artworks.length - shown.length);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onRemindLater();
      }}
    >
      <Dialog.Content className="max-w-[480px]" aria-describedby={undefined}>
        <div className="p-[36px_38px_30px] text-center font-sans">
          <div
            className="mx-auto mb-5 grid h-[76px] w-[76px] place-items-center rounded-full"
            style={{
              background: 'rgba(227,165,73,0.15)',
              border: '1px solid rgba(227,165,73,0.36)',
              color: AMBER,
            }}
          >
            <AccessTimeIcon style={{ fontSize: 36 }} />
          </div>
          <Dialog.Title className="text-[22px] font-bold text-white">{headline}</Dialog.Title>
          <div className="mx-auto mt-[11px] max-w-[400px] text-[14.5px] leading-[1.55] text-muted">
            Sign up free to keep{' '}
            <b className="text-body">
              {trackCount} {trackCount === 1 ? 'track' : 'tracks'}
            </b>{' '}
            and <b className="text-body">{mbUsed} MB</b> of work. Once the session ends, they’re
            gone.
          </div>
          {(shown.length > 0 || overflow > 0) && (
            <div className="m-[22px_0_4px] flex justify-center gap-[10px]">
              {shown.map((a, idx) => (
                <Thumbnail key={idx} art={a} />
              ))}
              {overflow > 0 && (
                <div
                  className="grid h-16 w-16 place-items-center rounded-[10px] text-[13px] font-semibold text-muted"
                  style={{ border: `1px dashed ${BORDER}` }}
                >
                  +{overflow}
                </div>
              )}
            </div>
          )}
          <div className="mt-[26px] flex flex-col gap-[11px]">
            <button
              type="button"
              onClick={onSignup}
              className="cursor-pointer rounded-[10px] border-0 p-[13px] text-[15px] font-semibold hover:brightness-[1.06]"
              style={{ background: AMBER, color: AMBER_INK }}
            >
              Sign up to keep everything
            </button>
            <UiButton variant="ghost" size="lg" fullWidth onClick={onRemindLater}>
              Remind me later
            </UiButton>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ExpireGraceModal;
